/**
 * src/help/GuidedHelp.js
 *
 * Guided-help engine for the Dashboard. Pure overlay: when inactive it renders
 * NOTHING and adds NO listeners, so it has effectively zero runtime cost on the
 * normal page. When active it dims the page, spotlights one real UI element at a
 * time, shows a small bright-yellow "what is this?" box with an arrow, and opens
 * a large detailed help modal.
 *
 * SCOPED, CHEAP INTERACTION MODEL (no global input capture):
 *   - The page is dimmed with FOUR rectangles that frame the current target
 *     (not an expensive 9999px box-shadow). Those rectangles absorb clicks on
 *     the dimmed page, so the rest of the UI cannot be triggered — WITHOUT any
 *     document-wide preventDefault/stopPropagation. Nothing outside the overlay
 *     is intercepted, so scrolling, the browser, and external tools work
 *     normally.
 *   - A transparent "hotspot" sits over ONLY the current target. Clicking it
 *     opens that target's modal; the real control underneath never receives the
 *     click, so its normal action is not performed — scoped to the target only.
 *   - Interactive targets (buttons, links, menu items) invite a click on the
 *     highlighted control. Informational targets (labels, values, status) show
 *     a "Continue" button in the yellow box instead of asking for a click.
 *
 * Yellow is the universal help colour, independent of the category theme.
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

const HELP = {
  yellow:     '#FFDD00',
  yellowDeep: '#F5B800',
  yellowSoft: 'rgba(255,221,0,0.28)',
  ink:        '#1A1500',
  dim:        'rgba(3,5,9,0.66)',
};

const STICKY_TOP = 172; // sticky ProductNav height
const PAD = 8;
const BOX_W = 300;
const GAP = 16;

function q(id) {
  if (!id) return null;
  try { return document.querySelector('[data-help-id="' + (window.CSS && CSS.escape ? CSS.escape(id) : id) + '"]'); }
  catch (e) { return document.querySelector('[data-help-id="' + id + '"]'); }
}

export default function GuidedHelp({ pageKey, manifest, open, startId, onClose }) {
  const steps = useMemo(() => {
    if (!open || !manifest) return [];
    return manifest.filter((s) => {
      if (!s.conditional) return true;
      if (q(s.target)) return true;
      if (s.revealVia && q(s.revealVia)) return true;
      return false;
    });
  }, [open, manifest]);

  const startIndex = useMemo(() => {
    if (!open) return 0;
    if (startId) { const i = steps.findIndex((s) => s.id === startId); if (i >= 0) return i; }
    return 0;
  }, [open, startId, steps]);

  const [index, setIndex] = useState(0);
  const [phase, setPhase] = useState('spot'); // 'spot' | 'modal'
  const [rect, setRect] = useState(null);
  const [tick, setTick] = useState(0);
  const modalRef = useRef(null);
  const nextBtnRef = useRef(null);

  const step = steps[index] || null;
  const total = steps.length;

  useEffect(() => { if (open) { setIndex(startIndex); setPhase('spot'); } }, [open, startIndex]);

  // Reveal a target that only exists once its parent is opened (⋯ menu, submenu).
  const reveal = useCallback((s) => {
    if (!s || q(s.target)) return;
    if (s.revealVia) { const p = q(s.revealVia); if (p) p.click(); }
  }, []);

  const measure = useCallback(() => {
    if (!step) return;
    const el = q(step.target) || q(step.revealVia);
    if (!el) { setRect(null); return; }
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, right: r.right, bottom: r.bottom, width: r.width, height: r.height });
  }, [step]);

  // On entering a step (spot phase): reveal if needed, scroll into view, measure.
  useLayoutEffect(() => {
    if (!open || phase !== 'spot' || !step) return;
    reveal(step);
    const el = q(step.target) || q(step.revealVia);
    if (el) {
      const r = el.getBoundingClientRect();
      if (r.top < STICKY_TOP + 12 || r.bottom > window.innerHeight - 12) {
        window.scrollTo({ top: Math.max(0, window.scrollY + r.top - (STICKY_TOP + 40)), behavior: 'smooth' });
        const t = setTimeout(() => setTick((x) => x + 1), 360);
        return () => clearTimeout(t);
      }
    }
    measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, index, tick]);

  // Keep the spotlight glued to the target while scrolling/resizing — ACTIVE
  // ONLY, spot phase only, one rAF per event, all removed on exit. No perpetual
  // loop, no global mouse listeners.
  useEffect(() => {
    if (!open || phase !== 'spot') return;
    let raf = 0;
    const onMove = () => { cancelAnimationFrame(raf); raf = requestAnimationFrame(measure); };
    window.addEventListener('scroll', onMove, { passive: true, capture: true });
    window.addEventListener('resize', onMove);
    return () => {
      window.removeEventListener('scroll', onMove, { capture: true });
      window.removeEventListener('resize', onMove);
      cancelAnimationFrame(raf);
    };
  }, [open, phase, measure]);

  // Keyboard: Esc exits, Enter/→ = Next in modal, Tab traps in modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); doExit(); return; }
      if (phase === 'modal' && (e.key === 'Enter' || e.key === 'ArrowRight')) { e.preventDefault(); doNext(); return; }
      if (phase === 'modal' && e.key === 'Tab' && modalRef.current) {
        const f = modalRef.current.querySelectorAll('button');
        if (f.length) {
          const first = f[0], last = f[f.length - 1];
          if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
          else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, phase, index, total]);

  useEffect(() => { if (phase === 'modal' && nextBtnRef.current) nextBtnRef.current.focus(); }, [phase, index]);

  function closeRevealedMenus() {
    // If we opened the ⋯ menu / submenu, close it via the card's own outside-click.
    if (q('menu-subject') || q('menu-state') || q('menu-variation') || q('var-standard')) {
      try { document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true })); } catch (e) {}
    }
  }

  const doNext = useCallback(() => {
    const cur = steps[index], nxt = steps[index + 1];
    const inMenu = (x) => x && (x.revealVia === 'build-menu' || x.revealVia === 'menu-variation' || x.interactionType === 'menu-open');
    if (inMenu(cur) && !inMenu(nxt)) closeRevealedMenus();
    if (index + 1 >= total) { doExit(); return; }
    setIndex((i) => i + 1);
    setPhase('spot');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index, steps, total]);

  const doBack = useCallback(() => { if (index > 0) { setIndex((i) => i - 1); setPhase('spot'); } }, [index]);

  const doExit = useCallback(() => {
    closeRevealedMenus();
    setPhase('spot');
    if (onClose) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, index]);

  if (!open || !step) return null;

  const informational = step.kind === 'informational';
  const stepNo = index + 1;

  // Spotlight frame geometry.
  const vw = window.innerWidth, vh = window.innerHeight;
  let t = 0, l = 0, r = vw, b = vh, haveRect = false;
  if (rect) {
    t = Math.max(0, rect.top - PAD); l = Math.max(0, rect.left - PAD);
    r = Math.min(vw, rect.right + PAD); b = Math.min(vh, rect.bottom + PAD);
    haveRect = true;
  }

  // Yellow box + arrow placement (choose the side with room).
  let box = null, arrow = null;
  if (haveRect) {
    const spaceBelow = vh - b, spaceAbove = t - STICKY_TOP, cx = (l + r) / 2;
    let side = spaceBelow >= 150 ? 'below' : spaceAbove >= 150 ? 'above' : (vw - r) >= BOX_W + GAP ? 'right' : 'left';
    if (side === 'below') { box = { top: b + GAP, left: clamp(cx - BOX_W / 2, 12, vw - BOX_W - 12) }; arrow = { top: b + 2, left: cx - 9, dir: 'up' }; }
    else if (side === 'above') { box = { bottom: vh - t + GAP, left: clamp(cx - BOX_W / 2, 12, vw - BOX_W - 12) }; arrow = { top: t - 18, left: cx - 9, dir: 'down' }; }
    else if (side === 'right') { box = { top: clamp(t, STICKY_TOP, vh - 160), left: r + GAP }; arrow = { top: (t + b) / 2 - 9, left: r + 2, dir: 'left' }; }
    else { box = { top: clamp(t, STICKY_TOP, vh - 160), left: Math.max(l - BOX_W - GAP, 12) }; arrow = { top: (t + b) / 2 - 9, left: l - 18, dir: 'right' }; }
  }

  const dimRect = (s) => ({ position: 'fixed', background: HELP.dim, pointerEvents: 'auto', zIndex: 20001, ...s });

  return (
    <div data-help-ui="1" role="dialog" aria-label="Guided help"
         style={{ position: 'fixed', inset: 0, zIndex: 20000, pointerEvents: 'none' }}>

      {/* Dim — four rectangles framing the target (cheap; absorb page clicks). */}
      {haveRect ? (
        <>
          <div aria-hidden="true" style={dimRect({ top: 0, left: 0, width: vw, height: t })} />
          <div aria-hidden="true" style={dimRect({ top: b, left: 0, width: vw, height: Math.max(0, vh - b) })} />
          <div aria-hidden="true" style={dimRect({ top: t, left: 0, width: l, height: Math.max(0, b - t) })} />
          <div aria-hidden="true" style={dimRect({ top: t, left: r, width: Math.max(0, vw - r), height: Math.max(0, b - t) })} />
          {/* Yellow highlight ring (visual only). */}
          <div aria-hidden="true" style={{
            position: 'fixed', top: t, left: l, width: r - l, height: b - t, borderRadius: 12,
            border: `3px solid ${HELP.yellow}`, boxShadow: `0 0 0 3px ${HELP.yellowSoft}`,
            pointerEvents: 'none', zIndex: 20002, transition: 'top 0.15s ease, left 0.15s ease, width 0.15s ease, height 0.15s ease',
          }} />
          {/* Clickable hotspot over ONLY the target. */}
          {phase === 'spot' && (
            <div
              data-help-ui="1"
              data-testid="help-hotspot"
              onClick={() => setPhase('modal')}
              title={informational ? undefined : 'Click to learn more'}
              style={{ position: 'fixed', top: t, left: l, width: r - l, height: b - t, zIndex: 20003, pointerEvents: 'auto', cursor: 'pointer', background: 'transparent' }}
            />
          )}
        </>
      ) : (
        <div aria-hidden="true" style={{ position: 'fixed', inset: 0, background: HELP.dim, pointerEvents: 'auto', zIndex: 20001 }} />
      )}

      {/* Small yellow context box + arrow (spot phase only). */}
      {phase === 'spot' && box && (
        <>
          <div aria-hidden="true" style={{ position: 'fixed', top: arrow.top, left: arrow.left, width: 0, height: 0, zIndex: 20010, pointerEvents: 'none', ...arrowStyle(arrow.dir) }} />
          <div data-help-ui="1" role="status" style={{
            position: 'fixed', width: BOX_W, ...pos(box), background: HELP.yellow, color: HELP.ink,
            borderRadius: 14, padding: '14px 16px', boxShadow: '0 14px 40px rgba(0,0,0,0.45)',
            border: `2px solid ${HELP.yellowDeep}`, pointerEvents: 'auto', zIndex: 20011,
          }}>
            <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', opacity: 0.7, marginBottom: 4 }}>Help · {stepNo} of {total}</div>
            <div style={{ fontSize: 17, fontWeight: 900, lineHeight: 1.2, marginBottom: 5 }}>{step.shortTitle}</div>
            <div style={{ fontSize: 14, fontWeight: 600, lineHeight: 1.4 }}>{step.shortText}</div>
            {informational ? (
              <button data-help-ui="1" onClick={() => setPhase('modal')} style={{
                marginTop: 10, background: HELP.ink, color: HELP.yellow, border: 'none', borderRadius: 9,
                padding: '8px 16px', fontSize: 13, fontWeight: 900, cursor: 'pointer',
              }}>Continue →</button>
            ) : (
              <div style={{ fontSize: 12, fontWeight: 700, marginTop: 8, opacity: 0.72 }}>Click the highlighted control to learn more →</div>
            )}
            <button data-help-ui="1" data-testid="help-close" onClick={doExit} aria-label="Exit help" style={{
              position: 'absolute', top: 6, right: 8, background: 'transparent', border: 'none', color: HELP.ink,
              fontSize: 16, fontWeight: 900, cursor: 'pointer', opacity: 0.6, pointerEvents: 'auto',
            }}>×</button>
          </div>
        </>
      )}

      {/* Large detailed modal. */}
      {phase === 'modal' && (
        <div data-help-ui="1" style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, pointerEvents: 'auto', zIndex: 20020 }}>
          <div ref={modalRef} role="dialog" aria-modal="true" aria-label={step.modalTitle} style={{
            width: 'min(72vw, 900px)', maxHeight: '82vh', overflowY: 'auto', background: '#12131b',
            border: `2px solid ${HELP.yellowDeep}`, borderRadius: 20, boxShadow: '0 30px 90px rgba(0,0,0,0.6), 0 0 0 6px rgba(255,221,0,0.10)',
            padding: '34px 40px 28px', color: '#F7F9FC',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: HELP.yellow, color: HELP.ink, fontWeight: 900, fontSize: 18, flexShrink: 0 }}>?</span>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: '0.12em', textTransform: 'uppercase', color: HELP.yellow }}>Help · Step {stepNo} of {total}</div>
            </div>
            <h2 style={{ fontSize: 32, lineHeight: 1.15, fontWeight: 900, margin: '0 0 18px', letterSpacing: '-0.3px' }}>{step.modalTitle}</h2>
            {step.modalBody.map((p, i) => (<p key={i} style={{ fontSize: 19, lineHeight: 1.65, color: '#D7DEEA', margin: '0 0 14px' }}>{p}</p>))}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14, marginTop: 24, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 10 }}>
                <button data-help-ui="1" data-testid="help-exit" onClick={doExit} style={btn.exit}>Exit Help</button>
                {index > 0 && <button data-help-ui="1" onClick={doBack} style={btn.back}>← Back</button>}
              </div>
              <button data-help-ui="1" data-testid="help-next" ref={nextBtnRef} onClick={doNext} style={btn.next}>{index + 1 >= total ? 'Finish' : 'Next →'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function clamp(v, lo, hi) { return Math.min(Math.max(v, lo), hi); }
function pos(box) { const o = {}; if (box.top != null) o.top = box.top; if (box.bottom != null) o.bottom = box.bottom; if (box.left != null) o.left = box.left; return o; }
function arrowStyle(dir) {
  const c = HELP.yellow;
  switch (dir) {
    case 'up':   return { borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderBottom: `16px solid ${c}` };
    case 'down': return { borderLeft: '9px solid transparent', borderRight: '9px solid transparent', borderTop: `16px solid ${c}` };
    case 'left': return { borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderRight: `16px solid ${c}` };
    default:     return { borderTop: '9px solid transparent', borderBottom: '9px solid transparent', borderLeft: `16px solid ${c}` };
  }
}
const btn = {
  next: { background: HELP.yellow, color: HELP.ink, border: 'none', borderRadius: 11, padding: '14px 30px', fontSize: 17, fontWeight: 900, cursor: 'pointer', boxShadow: '0 6px 18px rgba(255,221,0,0.28)' },
  exit: { background: 'transparent', color: 'rgba(255,255,255,0.7)', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 11, padding: '13px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
  back: { background: 'rgba(255,255,255,0.06)', color: 'rgba(255,255,255,0.85)', border: '1px solid rgba(255,255,255,0.16)', borderRadius: 11, padding: '13px 20px', fontSize: 15, fontWeight: 700, cursor: 'pointer' },
};
